import { ParsedUrlQuery } from 'node:querystring';
import { type i18n } from 'i18next';
import { AdKind } from '@/api/types/common.types';
import { getBaseUrl, setSuffix } from '@/utils/helpers';
import {
  FinishedPayment,
  PaymentMethod,
  PaymentService,
} from '@/api/types/payments.types';
import { QueryPayment } from '@/views/Wallet/Finish.types';
import { ROUTES } from '@/constants/routes';
import { BUMPS_PER_DAY } from '@/components/Ad/Promotions/Promotions.constants';

export const getPartialServiceLabel = (
  i18n: i18n,
  value: string | number,
  objKey = 'n',
  tKey = 'common.n_days'
) => i18n.t(setSuffix(i18n, value.toString(), tKey), { [objKey]: value });

export function getServiceLabel(i18n: i18n, payment: QueryPayment) {
  const { Bumped, Vipped, Premium } = AdKind;
  const { serviceType, period, categoryName, adsCount } = payment;

  if (serviceType === Bumped) {
    const label = getPartialServiceLabel(
      i18n,
      +period * BUMPS_PER_DAY,
      'n',
      'pages.ad.service_period_price'
    );
    return `${i18n.t('pages.ad.up_in_search')} / ${label}`;
  }

  if (serviceType && [Vipped, Premium].includes(serviceType)) {
    const service = serviceType === Vipped ? 'vip' : 'premium';
    return `${i18n.t(`pages.ad.${service}`)} ${getPartialServiceLabel(
      i18n,
      period,
      'days'
    )}`;
  }
  if (categoryName && adsCount) {
    return `${categoryName} - ${getPartialServiceLabel(
      i18n,
      adsCount,
      'n',
      'common.n_ads'
    )} / ${payment.price} ₼`;
  }

  if (payment.privateAccountId) return i18n.t('common.add_balance');

  return '';
}

export function mapQueryStringToPaymentData(
  query: ParsedUrlQuery
): QueryPayment | null {
  const {
    price,
    date,
    id,
    adId,
    serviceType,
    period,
    paymentMethod,
    categoryName,
    adsCount,
    privateAccountId,
  } = query;

  if (!price || !date || !id || !paymentMethod) {
    return null;
  }

  const baseFields = {
    price: String(price),
    date: String(date).replace(' ', '+'),
    id: String(id),
    paymentMethod: String(paymentMethod) as PaymentMethod,
  };

  if (serviceType && period) {
    return {
      ...baseFields,
      adId: String(adId),
      serviceType: String(serviceType) as AdKind,
      period: String(period),
    };
  }

  if (categoryName && adsCount !== undefined) {
    return {
      ...baseFields,
      adId: String(adId),
      categoryName: String(categoryName),
      adsCount: String(adsCount),
    };
  }

  if (privateAccountId) {
    return {
      ...baseFields,
      privateAccountId: String(privateAccountId),
    };
  }

  return null;
}

export function mapPaymentDataToUrl(paymentData: QueryPayment) {
  const { id, price, date, paymentMethod } = paymentData;
  const baseUrl = getBaseUrl(window.location.href);
  const urlParams = new URLSearchParams({
    id,
    price,
    date,
    paymentMethod,
  });

  if (
    'serviceType' in paymentData &&
    'period' in paymentData &&
    'adId' in paymentData &&
    paymentData.serviceType &&
    paymentData.period &&
    paymentData.adId
  ) {
    urlParams.append('serviceType', paymentData.serviceType);
    urlParams.append('period', paymentData.period);
    urlParams.append('adId', paymentData.adId);
  } else if (
    'categoryName' in paymentData &&
    'adsCount' in paymentData &&
    'adId' in paymentData &&
    paymentData.categoryName &&
    paymentData.adsCount &&
    paymentData.adId
  ) {
    urlParams.append('categoryName', paymentData.categoryName);
    urlParams.append('adsCount', paymentData.adsCount.toString());
    urlParams.append('adId', paymentData.adId);
  } else if (
    'privateAccountId' in paymentData &&
    paymentData.privateAccountId
  ) {
    urlParams.append('privateAccountId', paymentData.privateAccountId);
  }

  return `${baseUrl}${ROUTES.walletFinish}?${urlParams.toString()}`;
}

export const isQueryPayment = (
  payment: FinishedPayment | QueryPayment
): payment is QueryPayment =>
  'id' in payment && !('paymentLegacyId' in payment);

export const getPaymentAmount = (
  payment: FinishedPayment | QueryPayment
): number => Number(isQueryPayment(payment) ? payment.price : payment.amount);

export const getPaymentDate = (
  payment: FinishedPayment | QueryPayment
): string => (isQueryPayment(payment) ? payment.date : payment.updatedAt);

export const getPaymentId = (
  payment: FinishedPayment | QueryPayment
): string | number =>
  isQueryPayment(payment) ? payment.id : payment.paymentLegacyId;

export const getPaymentTargetId = (
  payment: FinishedPayment | QueryPayment
): string | number =>
  isQueryPayment(payment)
    ? payment.privateAccountId ?? payment.adId
    : payment.targetLegacyId;

export const getPaymentServiceName = (
  payment: FinishedPayment | QueryPayment,
  i18n: i18n
): string =>
  isQueryPayment(payment)
    ? getServiceLabel(i18n, payment)
    : payment.serviceName;

export const isPrivateAccount = (
  payment: FinishedPayment | QueryPayment
): boolean =>
  'privateAccountId' in payment ||
  ('serviceId' in payment &&
    payment.serviceId === PaymentService.Add_balance_custom);
